import type { ApolloClient } from '@apollo/client'
import { getNode, type ViewQuery, type ViewQueryList } from '@salescore/core'
import { message } from 'antd'
import { Array, Effect, Order, pipe, Record } from 'effect'
import { isNullish, isUndefined } from 'effect/Predicate'
import { t } from 'i18next'

import { searchRelation } from '../../recoil/hooks/searchRelation'
import { fetchChildRecordNode } from '../../recoil/records/mutations/fetchChildNode'
import type { RelationChange } from '../../recoil/records/mutations/updateRecordNodeRelations'
import { getReferencingNode } from '../../rsheet/util/getReferencingNode'
import type { RecordChange } from '../../rsheet/util/updateViewRecords'
import type { RelationPaste } from './relationPaste'

export type RecordChangeForRelation = RecordChange & { relationPaste: RelationPaste | undefined }

// 参照列ペーストの解決方法。id 解決済みなら検索不要、ラベル検索ならSQLと検索キーを持つ。
type RelationResolution =
  { kind: 'resolved'; id: string; label: string } | { kind: 'search'; sql: string; searchKey: string } | undefined

// オブジェクトが 1:N の関係のオブジェクト
export const generateParentToChildRelationChanges = async (
  initialRecordChanges: RecordChangeForRelation[],
  query: ViewQueryList | undefined,
  client: ApolloClient<object>,
  organizationId: string,
): Promise<{
  recordChanges: RecordChange[]
  relationChanges: RelationChange[]
  relationChangeKeySet: Set<string>
  errors: Array<{ message: string }>
}> => {
  if (query === undefined) {
    return { recordChanges: initialRecordChanges, relationChanges: [], relationChangeKeySet: new Set(), errors: [] }
  }
  // 同一解決（同じ検索 / 同じ id）はグルーピングして、検索・取得リクエスト回数を最小限にする
  const groupedChanges: Array<
    Array.NonEmptyArray<{
      recordChange: RecordChangeForRelation
      resolution: RelationResolution
    }>
  > = pipe(
    initialRecordChanges,
    Array.map((recordChange) => ({
      recordChange,
      resolution: prepareRelationResolution(recordChange, query),
    })),
    Array.groupBy((x) => resolutionGroupKey(x.resolution)),
    Record.values,
  )
  if (initialRecordChanges.length > 50 || groupedChanges.length > 3) {
    void message.warning(
      t(`更新するレコード数が多い場合や、参照列へのペーストには時間がかかります。完了後に保存してください。`),
    )
  }

  const results = await Effect.runPromise(
    Effect.all(
      groupedChanges.map((group) =>
        Effect.tryPromise({
          try: async () => {
            const [representative] = group
            // 解決不要な場合（null・非文字列・labelNodePath 不在など）
            if (representative.resolution === undefined) {
              return {
                recordChanges: group.map(({ recordChange }) => recordChange),
                relationChanges: [],
              }
            }
            // 子ブロックのノードを取得する
            const referencedNode = getReferencingNode({
              field: representative.recordChange.field,
              queryTree: query.tree,
            })

            if (referencedNode === undefined) {
              return {
                recordChanges: group.map(({ recordChange }) => recordChange),
                relationChanges: [],
              }
            }
            // id 解決済みなら検索をスキップし、id をそのまま採用する。
            // ラベル検索の場合のみ searchKey（ラベル）から SFA 上のレコード ID を検索する。
            const bestMatch =
              representative.resolution.kind === 'resolved'
                ? { value: representative.resolution.id, label: representative.resolution.label }
                : await resolveBestMatchByLabel(
                    client,
                    organizationId,
                    representative.resolution.sql,
                    representative.resolution.searchKey,
                  )
            if (bestMatch === undefined) {
              // 検索でヒットしなかった場合のみ警告する（id 解決済みでは bestMatch は常に存在する）
              if (representative.resolution.kind === 'search') {
                void message.warning(
                  t(`{{searchKey}}は見つかりませんでした`, { searchKey: representative.resolution.searchKey }),
                )
              }
              return {
                recordChanges: [], // 見つからなかった場合は、該当するRecordChange自体を無かったことにする
                relationChanges: [],
              }
            }
            // 検索結果をもとに、RecordChange の value （と label）を更新する
            const updatedRecordChanges = group.map(({ recordChange }) => ({
              ...recordChange,
              value: bestMatch.value,
              label: bestMatch.label,
            }))
            // SFA上のレコードIDから、RecordNodeを取得する
            const fetchedChildRecordNode = await fetchChildRecordNode(
              referencedNode,
              bestMatch.value,
              query,
              client,
              representative.recordChange.field,
            )
            if (fetchedChildRecordNode === undefined) {
              return {
                recordChanges: [], // RecordNodeが取得できなかった場合は、該当するRecordChange自体を無かったことにする
                relationChanges: [],
              }
            }
            const newRelationChanges = group.map(({ recordChange }) => ({
              node: referencedNode,
              rowIndex: recordChange.rowIndex,
              innerRowIndex: recordChange.innerRowIndex,
              recordNode: fetchedChildRecordNode,
            }))
            return {
              recordChanges: updatedRecordChanges,
              relationChanges: newRelationChanges,
            }
          },
          catch: (error) => ({ message: String(error) }),
        }),
      ),
      {
        mode: 'result',
        concurrency: 3,
      },
    ),
  )
  const [successes, errors] = Array.separate(results)
  const recordChanges = successes.flatMap((success) => success.recordChanges)
  const relationChanges = successes.flatMap((success) => success.relationChanges)

  const relationChangeKeySet = new Set(
    relationChanges.map((rc) =>
      keyFunction({
        nodePath: rc.node.path,
        rowIndex: rc.rowIndex,
        innerRowIndex: rc.innerRowIndex,
      }),
    ),
  )

  return { recordChanges, relationChanges, relationChangeKeySet, errors }
}

// 参照列ペーストの解決方法を決める。id 解決済み（同一参照先エンティティからのコピー）なら検索不要。
// そうでなければ従来どおり value（ラベル）から検索する。
const prepareRelationResolution = (recordChange: RecordChangeForRelation, query: ViewQuery): RelationResolution => {
  if (recordChange.relationPaste?.kind === 'resolvedById') {
    return { kind: 'resolved', id: recordChange.relationPaste.id, label: recordChange.relationPaste.label }
  }
  const search = prepareSearchKey(recordChange, query)
  return search === undefined ? undefined : { kind: 'search', sql: search.sql, searchKey: search.searchKey }
}

// 同一解決をまとめるためのグループキー。resolved は id 単位、search は sql + searchKey 単位でまとめる。
const resolutionGroupKey = (resolution: RelationResolution): string =>
  resolution === undefined
    ? ''
    : // eslint-disable-next-line sonarjs/no-nested-conditional
      resolution.kind === 'resolved'
      ? `resolved:${resolution.id}`
      : `search:${JSON.stringify({ sql: resolution.sql, searchKey: resolution.searchKey })}`

// ラベルからレコードを検索し、最適な 1 件を返す。LIKE 検索のため複数ヒットしうる。
const resolveBestMatchByLabel = async (
  client: ApolloClient<object>,
  organizationId: string,
  sql: string,
  searchKey: string,
): Promise<{ value: string; label: string } | undefined> => {
  const searchResult = await searchRelation(client, organizationId)(sql, searchKey, undefined, undefined)
  const results = searchResult?.values ?? []
  // ラベル文字数が最小のものを最適と仮定して選択する
  const [bestMatch] = pipe(results, Array.sortBy(Order.mapInput(Order.Number, (x) => x.label.length)))
  return bestMatch
}

function prepareSearchKey(
  recordChange: RecordChange,
  query: ViewQuery,
):
  | {
      sql: string
      searchKey: string
    }
  | undefined {
  const {
    field: { read },
    value: searchKey,
  } = recordChange
  if (isNullish(searchKey)) {
    return
  }
  if (typeof searchKey !== 'string') {
    void message.warning(t(`参照項目に対して、文字列以外はペーストできません`))
    return
  }

  if (isUndefined(read.labelNodePath)) {
    return
  }
  const referencedNode = getNode(query.tree, read.labelNodePath)
  if (isUndefined(referencedNode) || isUndefined(read.searchSql)) {
    return
  }
  return {
    sql: read.searchSql,
    searchKey,
  }
}

export const keyFunction = ({
  nodePath,
  rowIndex,
  innerRowIndex,
}: {
  nodePath: readonly string[]
  rowIndex: number
  innerRowIndex: number
}): string => `${nodePath.join('|')}-${rowIndex}-${innerRowIndex}`
